import './styles.css';
import morphdom from 'morphdom';
import { connect, type AppSocket } from './socket';
import { getOrCreateSessionId, getStoredName } from './state';
import type { RoomListing, RoomState } from '../../shared/types.js';
import { Wheel } from './wheel';
import { fireConfetti } from './confetti';
import { showToast } from './dom';
import { onRouteChange, parseRoute, type Route, navigate } from './router';
import { buildHomePage } from './pages/home';
import { buildRoomPage } from './pages/room';
import { buildAdminPage } from './pages/admin';
import { consumeAdminTokenFromHash } from './admin';
import { onLocaleChange, t } from './i18n';

consumeAdminTokenFromHash();

type FullState = RoomState & { youId: string };

const sessionId = getOrCreateSessionId();
const socket: AppSocket = connect();

let route: Route = parseRoute(window.location.pathname);
let roomState: FullState | null = null;
let roomList: RoomListing[] | null = null;
let lastSpinId: number | null = null;
let wheel: Wheel | null = null;
let mounted = false;

const root = document.getElementById('app')!;

// ---- routing ----
onRouteChange((newRoute) => {
  const from = route;
  route = newRoute;
  if (from.name === 'room' && newRoute.name !== 'room') {
    socket.emit('leaveRoom');
    roomState = null;
    if (wheel) {
      wheel.reset();
      wheel = null;
    }
  }
  syncSocketToRoute();
  render();
});

function syncSocketToRoute() {
  if (!socket.connected) return;
  if (route.name === 'room') {
    socket.emit('joinRoom', { code: route.code, sessionId, name: getStoredName() });
  } else if (route.name === 'home') {
    socket.emit('listRooms');
  }
  // admin route is REST-only; no socket sync needed.
}

// ---- socket lifecycle ----
socket.on('connect', () => {
  syncSocketToRoute();
});

socket.on('state', (s) => {
  const prev = roomState;
  roomState = s;
  if (s.spin && s.spin.startedAt !== lastSpinId) {
    lastSpinId = s.spin.startedAt;
  }
  if (s.phase === 'lobby' && prev && prev.phase !== 'lobby') {
    if (wheel) wheel.reset();
    lastSpinId = null;
  }
  if (s.phase === 'result' && prev?.phase !== 'result') {
    fireConfetti();
  }
  render();
});

socket.on('roomList', (rooms) => {
  roomList = rooms;
  if (route.name === 'home') render();
});

socket.on('roomCreated', ({ code }) => {
  navigate(`/r/${code}`);
});

socket.on('errorMsg', (msg) => {
  showToast(msg, 'error');
  // If we were attempting to join a room that didn't exist, bounce home.
  if (route.name === 'room' && /not found/i.test(msg)) {
    navigate('/');
  }
});

socket.on('notice', (msg) => showToast(msg, 'info'));
socket.on('disconnect', () => showToast(t('disconnected'), 'warn'));

// Re-render whenever the user toggles language.
onLocaleChange(() => render());

// ---- render dispatch ----
function render() {
  const newRoot = pickPage();
  if (!mounted) {
    root.replaceChildren(newRoot);
    mounted = true;
  } else {
    morphdom(root.firstElementChild!, newRoot, {
      // Keyed list reconciliation: any element with `data-key` (set on tiles,
      // master-list rows, participants, etc.) matches by key instead of by
      // position, which keeps event listeners attached to the right element
      // when the list grows or items toggle child structure.
      getNodeKey(node) {
        if (node instanceof HTMLElement) {
          return node.getAttribute('data-key') || node.id || undefined;
        }
        return undefined;
      },
      onBeforeElUpdated(fromEl, toEl) {
        if (
          fromEl === document.activeElement &&
          (fromEl instanceof HTMLInputElement || fromEl instanceof HTMLTextAreaElement)
        ) {
          if (fromEl.className !== toEl.className) fromEl.className = toEl.className;
          const toInput = toEl as HTMLInputElement | HTMLTextAreaElement;
          if (fromEl.disabled !== toInput.disabled) fromEl.disabled = toInput.disabled;
          return false;
        }
        if (fromEl instanceof HTMLCanvasElement && fromEl.classList.contains('wheel-canvas')) {
          return false;
        }
        if (fromEl.isEqualNode(toEl)) return false;
        return true;
      },
    });
  }
  afterRender();
}

function pickPage(): HTMLElement {
  if (route.name === 'home') {
    return buildHomePage({ socket, rooms: roomList });
  }
  if (route.name === 'admin') {
    return buildAdminPage(render);
  }
  // route.name === 'room'
  if (!roomState || roomState.meta.id !== route.code) {
    const wrap = document.createElement('div');
    wrap.className = 'connecting layout-empty';
    wrap.textContent = t('joiningRoom', { code: route.code });
    return wrap;
  }
  return buildRoomPage(roomState, { socket });
}

function afterRender() {
  if (route.name !== 'room' || !roomState) return;
  if (roomState.phase === 'spinning' || roomState.phase === 'result') {
    const canvas = document.querySelector<HTMLCanvasElement>('.wheel-canvas');
    if (canvas) {
      if (!wheel || wheel['canvas'] !== canvas) wheel = new Wheel(canvas);
      wheel.setState(roomState);
      if (roomState.phase === 'spinning' && roomState.spin) {
        wheel.spinTo(roomState.spin.finalRotation, roomState.spin.durationMs, roomState.spin.startedAt);
      } else if (roomState.phase === 'result' && roomState.spin) {
        wheel['rotation'] = roomState.spin.finalRotation;
        wheel['draw']?.();
      }
    }
  }
}

// Refresh the active rooms list every 10 s while on the home page so the
// player counts and "X min ago" stay live without server pushes for every
// minor change. (Server still pushes on create/delete/listed-flip.)
setInterval(() => {
  if (route.name === 'home' && socket.connected) socket.emit('listRooms');
}, 10_000);

render();
